"use client";

import { useGetMyLicensesQuery, useGetUserQuery } from "@/store/userApi";

/**
 * Whether the shopper looking at the page already satisfies the dental-license
 * requirement. "unknown" while we are still resolving it — the badge stays hidden
 * then, so a licensed shopper never sees it flash in and back out.
 */
export type UserLicenseStatus = "unknown" | "valid" | "invalid";

/** A license with no expiration date (DEA) never expires. */
const licenseIsUnexpired = (license: Account.Licenses.Licence): boolean =>
  !license.ExpirationDate || new Date(license.ExpirationDate) >= new Date();

export function useUserLicenseStatus(): UserLicenseStatus {
  const { data: user, isLoading: userIsLoading } = useGetUserQuery();

  // Only an authenticated, non-guest account can hold a license. `user` is undefined both
  // while the query is in flight and when it fails, so the isLoading branch below has to be
  //  read first.
  const isLoggedIn = user?.IsGuestUser === false && user?.isAuthorized === true;

  // Skipped unless that holds: /my-licenses 401s without an authenticated session, so an
  // unguarded call is a guaranteed failed request on every anonymous or expired page view.
  const { data: licenses = [], isLoading: licensesAreLoading } = useGetMyLicensesQuery(undefined, {
    skip: !isLoggedIn,
  });

  if (userIsLoading) return "unknown";
  // Guest, anonymous, or a failed user fetch — fail open and let the badge show.
  if (!isLoggedIn) return "invalid";
  if (licensesAreLoading) return "unknown";

  return licenses.some(licenseIsUnexpired) ? "valid" : "invalid";
}

/**
 * The global License Required badge rule: show it only when the product requires a
 * license AND we have confirmed this shopper does not have a valid one.
 *
 * Call sites whose badges live in a conditional container must gate that container with
 * this hook too, so it collapses instead of leaving an empty row of gap behind.
 */
export function useShowLicenseRequiredBadge(licenseIsRequired: boolean): boolean {
  // Called unconditionally: `licenseIsRequired` flips between renders as vendor options
  // resolve, so short-circuiting it would change the hook order mid-mount.
  const status = useUserLicenseStatus();
  return licenseIsRequired && status === "invalid";
}
